import React, { FC, useState, useEffect, useRef } from 'react';
import {UserManager, User} from 'oidc-client';
import {logger} from "../common/logger";
import { handleSignOut } from '../common/handleSignOut';
import { NonMandatory } from '../common';
import { Timeout } from '@communicare/medications/Shared/Types';
import { MedsUserManager } from "../../features/auth/MedsUserManager";

export interface Location {
    search: string;
    hash: string;
}

export interface AuthProviderSignOutProps {
    signoutRedirect?: boolean | unknown;
}

export interface AuthProviderProps {
    userManager?: UserManager;
    authority?: string;
    clientId?: string;
    clientSecret?: string;
    redirectUri?: string;
    silentRedirectUri?: string;
    postLogoutRedirectUri?: string;
    responseType?: string;
    scope?: string;
    location?: Location;
    autoSignIn?: boolean;
    automaticSilentRenew?: boolean;
    loadUserInfo?:boolean;
    popupWindowFeatures?: string;
    popupRedirectUri?: string;
    popupWindowTarget?:string;
    onBeforeSignIn?: () => void;
    onSignIn?: (userData: [User | null,string|null]) => Promise<void> | void;
    onSignOut?: (options?: AuthProviderSignOutProps) => Promise<void> | void;
    loginTimeout:number;
}

export interface AuthContextProps {
    signIn: (args?: unknown) => Promise<void>;
    signInPopup: () => Promise<void>
    signOut: () => Promise<void>;
    signOutRedirect: (args?: unknown) => Promise<void>;
    userManager: UserManager;
    userData?: {
        email:NonMandatory<string>;
        userName:NonMandatory<string>;
        familyName:NonMandatory<string>;
        givenName:NonMandatory<string>;
    }|null,
    isLoading: boolean;
    saveCurrentPath:(url:string|null)=>void;
    getSavedUrl:()=>string|null;
}

export const AuthContext = React.createContext<AuthContextProps | undefined>(undefined);

/**
 * @private
 * @hidden
 * @param location
 */
export const hasCodeInUrl = (location: Location): boolean => {
    const searchParams = new URLSearchParams(location.search);
    const hashParams = new URLSearchParams(location.hash.replace('#', '?'));

    return Boolean(
        searchParams.get('code') ||
        searchParams.get('id_token') ||
        searchParams.get('session_state') ||
        hashParams.get('code') ||
        hashParams.get('id_token') ||
        hashParams.get('session_state'),
    );
};

/**
 * @private
 * @hidden
 * @param props
 */
export const initUserManager = (props: AuthProviderProps): MedsUserManager => {
    if (props.userManager) return props.userManager;
    const {
        authority,
        clientId,
        clientSecret,
        redirectUri,
        silentRedirectUri,
        postLogoutRedirectUri,
        responseType,
        scope,
        automaticSilentRenew,
        loadUserInfo,
        popupWindowFeatures,
        popupRedirectUri,
        popupWindowTarget,
    } = props;
    return new MedsUserManager({
        authority,
        client_id: clientId,
        client_secret: clientSecret,
        redirect_uri: redirectUri,
        silent_redirect_uri: silentRedirectUri || redirectUri,
        post_logout_redirect_uri: postLogoutRedirectUri || redirectUri,
        response_type: responseType || 'code',
        scope: scope || 'openid',
        loadUserInfo: loadUserInfo != undefined ? loadUserInfo : true,
        popupWindowFeatures: popupWindowFeatures,
        popup_redirect_uri: popupRedirectUri,
        popupWindowTarget: popupWindowTarget,
        automaticSilentRenew,
    });
};


const currentUrlKey = 'CURRENT_URL';

export const AuthProvider: FC<AuthProviderProps> = ({
                                                        children,
                                                        onBeforeSignIn,
                                                        onSignIn,
                                                        onSignOut,
                                                        location = window.location,
                                                        ...props
                                                    }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [userData, setUserData] = useState<User | null>(null);
    const [userManager] = useState<UserManager>(initUserManager(props));
    const [currentUrl, setCurrentUrl] = useState<string|null>(window.sessionStorage.getItem(currentUrlKey));
    
    
    const signOutHooks = async (): Promise<void> => {
        setUserData(null);
        onSignOut && onSignOut();
    };
    const signInPopupHooks = async (): Promise<void> => {
        const userFromPopup = await userManager.signinPopup();
        setUserData(userFromPopup);
        onSignIn && onSignIn([userFromPopup,currentUrl]);
        await userManager.signinPopupCallback();
    };

    const isMountedRef = useRef(true);
    const isIdle = useRef<'Idle'|'Actioned'>('Idle');
    const timeout = useRef<Timeout|null>(null);
    
    const configureTimeout=(sessionTimeout:number)=>{
        if (timeout.current) {            
            clearTimeout(timeout.current);
        }        
        timeout.current = setTimeout(()=>{
            handleSignOut({reason: "Token expired", url: window.location.href});
        },sessionTimeout);
    }

    const getUser = async (): Promise<void> => {
        setIsLoading(true);
        /**
         * Check if the user is returning back from OIDC.
         */
        if (hasCodeInUrl(location)) {
            const user = await userManager.signinCallback();
            logger.log("Expires in",user?.expires_in);
            configureTimeout(props.loginTimeout);
            setUserData(user);
            setIsLoading(false);
            onSignIn && onSignIn([user,currentUrl]);
            setCurrentUrl(null);
            return;
        }
        
        const user = await userManager!.getUser();
        if (!user || user.expired) {
            onBeforeSignIn && onBeforeSignIn();
            await userManager.signinRedirect();
        } else if (isMountedRef.current) {
            if (!userData) {
                configureTimeout(props.loginTimeout);
            }
            setUserData(user);
            setIsLoading(false);
        }
        return;
    };
    
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
            isIdle.current = "Idle";
        };
    }, []);
    
    useEffect(()=>{
        if (currentUrl){
            window.sessionStorage.setItem(currentUrlKey,currentUrl)    
        }else{
            window.sessionStorage.removeItem(currentUrlKey);
        }
        
    },[currentUrl]);
    
    useEffect(()=>{
        const refresher = async ()=>{
            return await userManager.signinSilent();
        };

        const refreshToken = async()=>{
            logger.log('Token expiring');

            let refreshInvoker = refresher();

            let user = await refreshInvoker;

            if (!user || user?.expired){
                handleSignOut({reason: "Token expired", url: window.location.href});
            }
        };
        userManager.events.addAccessTokenExpiring(refreshToken);
        
        return ()=>{
            userManager.events.removeAccessTokenExpiring(refreshToken);
            
        }
    },[]);
    
    useEffect(() => {
        // for refreshing react state when new state is available in e.g. session storage
        const updateUserData = async () => {
            const user = await userManager.getUser();
            isMountedRef.current && setUserData(user);
        };
        
        
        userManager.events.addUserLoaded(updateUserData);
        
        return () => {
            userManager.events.removeUserLoaded(updateUserData);
        }
    }, [userManager]);
    
    useEffect(()=>{
        window.document.addEventListener('click',handleClickAway);
        return ()=>window.document.removeEventListener('click',handleClickAway);
    },[]);
    const handleClickAway=()=>{        
        userManager.getUser().then(user=>{
            if (user) {
                configureTimeout(props.loginTimeout);   
            }
        });
        
    };

    return (
        <AuthContext.Provider
            value={{
                signIn: async (args: unknown): Promise<void> => {
                    await getUser();
                },
                signInPopup: async (): Promise<void> => {
                    await signInPopupHooks();
                },
                signOut: async (): Promise<void> => {
                    await userManager!.removeUser();
                    await signOutHooks();
                },
                signOutRedirect: async (args?: unknown): Promise<void> => {
                    await userManager!.signoutRedirect(args);
                    await signOutHooks();
                },
                userManager,
                userData:userData?{
                    email:userData?.profile?.emails[0],
                    familyName:userData?.profile?.family_name,
                    givenName:userData?.profile?.given_name,
                    userName:userData?.profile?.name

                }:null,
                isLoading,
                saveCurrentPath:(url:string|null)=>{  
                    
                    //Storing payload key in localstorage to fetch and use while shared user relogin for S8 prescription
                    let params = new URLSearchParams(url ? url.split('?')[1] : '');                    
                    let payloadKey = params.get("payloadKey"); 
                    localStorage.setItem('reloginPayloadKey', payloadKey || '');                    
                    setCurrentUrl(url);
                },
                getSavedUrl:()=>currentUrl
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};